import { useContext, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { ComponentsContext, IComponentsContext } from "@/contexts";

import { FieldItem } from "./FieldItem";

interface DraggableFieldProps {
  field: AvailableField;
  disabled: boolean;
}

function DraggableField({ field, disabled }: DraggableFieldProps) {
  const isDisabled = disabled || !!field.readOnly;
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `available-${field.id}`,
      data: {
        ...field,
      },
      disabled: isDisabled,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDisabled ? "field-item-disabled" : ""}
      {...(!isDisabled && { ...listeners, ...attributes })}
    >
      <FieldItem field={field} />
    </div>
  );
}

import {
  // ToDo: fix typescript error
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  AvailableField,
  AvailableFieldsProps as BaseFieldsProps,
} from "../types";
import { useFormLayoutLabels } from "@/contexts/translation.context";

interface AvailableFieldsProps {
  fields: BaseFieldsProps;
  disabled: boolean;
}

export function AvailableFields({ fields, disabled }: AvailableFieldsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const { SearchBox } = useContext<IComponentsContext>(ComponentsContext);
  const labels = useFormLayoutLabels();

  const systemFields = fields.systemFields;
  const customFields = fields.customFields;

  // Convert system fields object to array and filter by search query
  const systemFieldsArray = Object.values(systemFields || {});
  const filteredSystemFields = searchQuery
    ? systemFieldsArray.filter((field) =>
        field.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : systemFieldsArray;

  // Convert custom fields object to array and filter by search query
  const customFieldsArray = Object.values(customFields || {});
  const filteredCustomFields = searchQuery
    ? customFieldsArray.filter((field) =>
        field.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : customFieldsArray;

  return (
    <div
      className={`available-fields ${disabled ? "available-fields-disabled" : ""}`}
    >
      <div className="fields-header">
        <div className="search-container">
          <SearchBox
            placeholder={labels.searchField}
            on-change={(e: any) => setSearchQuery(e.target.value)}
            disabled={disabled}
            value={searchQuery}
          />
        </div>
      </div>
      <div>
        {/* System Fields */}
        <div className="field-category">
          <div className="category-header">
            <h3 className="category-title">{labels.systemFields}</h3>
            <span className="category-count">
              {filteredSystemFields.length}
            </span>
          </div>
          <div className="category-fields">
            {filteredSystemFields.map((field) => (
              <DraggableField
                key={field.id}
                field={field}
                disabled={disabled}
              />
            ))}
          </div>
        </div>

        {/* Custom Fields */}
        <div className="field-category">
          <div className="category-header">
            <h3 className="category-title">{labels.customFields}</h3>
            <span className="category-count">
              {filteredCustomFields.length}
            </span>
          </div>
          <div className="category-fields">
            {filteredCustomFields.map((field) => (
              <DraggableField
                key={field.id}
                field={field}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
